import React, { useState } from "react";
import { stratificationService } from "../../services/stratification.services";
import { Pencil } from "../Icons/Pencil";
import { FloppyDisk } from "../Icons/FloppyDisk";
import { TrashCan } from "../Icons/TrashCan";

export const KeywordsSection = ({ items, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const handleAdd = async () => {
    if (!newKeyword.trim()) return;
    setSaving(true);
    try {
      await stratificationService.createKeyword({ keyword: newKeyword.trim() });
      setNewKeyword("");
      setShowAddForm(false);
      onRefresh();
    } catch (err) {
      console.error("Error creating keyword:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Opravdu chcete odstranit toto klíčové slovo?")) return;
    try {
      await stratificationService.deleteKeyword(id);
      onRefresh();
    } catch (err) {
      console.error("Error deleting keyword:", err);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.keyword || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleSaveEdit = async (id) => {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      await stratificationService.updateKeyword(id, {
        keyword: editValue.trim(),
      });
      cancelEdit();
      onRefresh();
    } catch (err) {
      console.error("Error updating keyword:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter") action();
    if (e.key === "Escape") {
      if (editingId) cancelEdit();
      else {
        setShowAddForm(false);
        setNewKeyword("");
      }
    }
  };

  return (
    <div className="strat-section">
      <div
        className="strat-section-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="strat-section-title">Klíčová slova</h3>
        <span className="strat-section-toggle">{isExpanded ? "−" : "+"}</span>
      </div>

      {isExpanded && (
        <div className="strat-section-content">
          <div className="strat-table-header">
            <div className="strat-header-cell strat-cell--keyword">
              Klíčové slovo
            </div>
            <div className="strat-header-cell strat-cell--actions" />
          </div>

          {items.length === 0 && !showAddForm && (
            <div className="strat-empty">
              Žádná klíčová slova nebyla přidána.
            </div>
          )}

          {items.map((item, idx) => {
            const isEditing = editingId === item.id;
            return (
              <div
                key={item.id}
                className={`strat-row ${idx % 2 === 1 ? "strat-row--odd" : ""} ${isEditing ? "strat-row--editing" : ""}`}
              >
                <div className="strat-cell strat-cell--keyword">
                  {isEditing ? (
                    <input
                      type="text"
                      className="strat-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, () => handleSaveEdit(item.id))
                      }
                      autoFocus
                    />
                  ) : (
                    item.keyword
                  )}
                </div>
                <div className="strat-cell strat-cell--actions">
                  {isEditing ? (
                    <>
                      <button
                        className="strat-action-btn strat-action-btn--save"
                        onClick={() => handleSaveEdit(item.id)}
                        title="Uložit"
                      >
                        <FloppyDisk className="strat-action-icon" />
                      </button>
                      <button
                        className="strat-action-btn strat-action-btn--cancel"
                        onClick={cancelEdit}
                        title="Zrušit"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="strat-action-btn strat-action-btn--edit"
                        onClick={() => startEdit(item)}
                        title="Upravit"
                      >
                        <Pencil className="strat-action-icon" />
                      </button>
                      <button
                        className="strat-action-btn strat-action-btn--delete"
                        onClick={() => handleDelete(item.id)}
                        title="Odstranit"
                      >
                        <TrashCan className="strat-action-icon" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {showAddForm && (
            <div className="strat-inline-form">
              <div className="strat-inline-field strat-inline-field--grow">
                <span className="strat-inline-label">Nové klíčové slovo</span>
                <input
                  type="text"
                  className="strat-input"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleAdd)}
                  placeholder="Zadejte klíčové slovo..."
                  autoFocus
                />
              </div>
              <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
                <button
                  className="strat-add-btn"
                  onClick={handleAdd}
                  disabled={!newKeyword.trim() || saving}
                >
                  {saving ? "Ukládání..." : "Uložit"}
                </button>
                <button
                  className="strat-add-btn strat-add-btn--secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewKeyword("");
                  }}
                >
                  Zrušit
                </button>
              </div>
            </div>
          )}

          <div className="strat-add-row">
            <button
              className="strat-add-btn"
              onClick={() => setShowAddForm(true)}
              disabled={showAddForm}
            >
              + Přidat klíčové slovo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
